'use client';

import { useRef } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { Img, useAssetUrl } from '@/lib/asset-url';

interface CardHeroProps {
  /** Background image URL */
  bgUrl?: string;
  /** Whether bgUrl is a video */
  bgIsVideo?: boolean;
  /** Foreground image overlay (e.g. event logo) */
  fgUrl?: string;
  fgAlt?: string;
  /** Date strip text at bottom of hero */
  dateStrip?: React.ReactNode;
  /** Campus name shown with MapPin icon in the date strip */
  campusStrip?: string;
  /** Color for the date/campus strip text */
  stripColor?: string;
  /** Additional elements rendered inside the hero (badges, indicators) */
  children?: React.ReactNode;
  /** Optional className for the outer wrapper */
  className?: string;
  /** Ref forwarded for video play/pause on hover */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
}

/**
 * Shared hero section for standard-layout cards.
 * Renders a 16:9 aspect-ratio container with BG image/video,
 * gradient overlay, optional FG image, and optional date strip.
 */
export function CardHero({
  bgUrl,
  bgIsVideo,
  fgUrl,
  fgAlt = '',
  dateStrip,
  campusStrip,
  stripColor,
  children,
  className = '',
  videoRef: externalVideoRef,
}: CardHeroProps) {
  const internalRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalVideoRef ?? internalRef;
  const resolve = useAssetUrl();

  return (
    <div className={`relative aspect-video w-full overflow-hidden ${className}`}>
      {bgIsVideo ? (
        <video
          ref={videoRef}
          src={bgUrl ? resolve(bgUrl) : bgUrl}
          autoPlay
          loop
          muted
          playsInline
          className="h-full w-full object-cover"
        />
      ) : bgUrl ? (
        <Img src={bgUrl} alt="" className="h-full w-full object-cover" />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/20" />

      {fgUrl && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <Img
            src={fgUrl}
            alt={fgAlt}
            className="max-h-[70%] max-w-[80%] object-contain drop-shadow-lg"
          />
        </div>
      )}

      {(dateStrip || campusStrip) && (
        <div
          className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 bg-gradient-to-t from-black/60 to-transparent px-4 pt-4 pb-2 text-[10px] font-semibold tracking-widest uppercase"
          style={{ color: stripColor ?? 'rgba(255,255,255,0.8)' }}
        >
          {campusStrip && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {campusStrip}
            </span>
          )}
          {dateStrip && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {dateStrip}
            </span>
          )}
        </div>
      )}

      {children}
    </div>
  );
}
